import {TableColumnsType} from 'antd';
import {generatePath} from 'react-router-dom';

import {appRoute} from '../../../../../app-route';
import {NavigationLink} from '../../../../../layout/navigation-link/navigation-link';
import {Locale} from '../../../../../provider/locale/locale';
import {
    ReviewsAnalysisTagType,
    ReviewsAnalysisUserTagType,
    ReviwsAnalysisUserTagTableDataType,
} from '../../../../../service/reivew-analysis/review-analytics-tags/review-analytics-tags-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {
    getAvgResponseDelayColumn,
    getRateColumn,
    getRepliedColumn,
    getRepliedWithPercentageColumn,
    getSentimentColumn,
    getTotalColumn,
    getTotalWithPercentageColumn,
    getTrendColumn,
} from '../../components/table/reviews-analysis-table-helper';

import {ConcordanceLink} from './concordance-link/concordance-link';
import {TopicTableDataType} from './keywords-and-topics-page-type';

export function mergeCompareData(
    data: Array<ReviewsAnalysisUserTagType>,
    compareData: Array<ReviewsAnalysisUserTagType>,
    compareName: string
): Array<ReviwsAnalysisUserTagTableDataType> {
    return data.flatMap(
        (dataItem: ReviewsAnalysisUserTagType, index: number): Array<ReviwsAnalysisUserTagTableDataType> => {
            const compareDataItem: ReviewsAnalysisUserTagType =
                compareData.find(({title}: ReviewsAnalysisUserTagType): boolean => title === dataItem.title) ||
                ({} as ReviewsAnalysisUserTagType);

            return [
                {
                    ...dataItem,
                    key: dataItem.title,
                    isCompareItem: false,
                    compareItem: compareDataItem,
                },
                {
                    ...compareDataItem,
                    key: `${compareDataItem.title || 'empty'}-${index}`,
                    title: compareName,
                    isCompareItem: true,
                    compareItem: dataItem,
                },
            ];
        }
    );
}

export function tagsPageTableColumns({
    timeUnit,
}: {
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum;
}): TableColumnsType<ReviewsAnalysisTagType> {
    return [
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__TAG" />,
            dataIndex: 'tag',
            key: 'tag',
            render: (_value: unknown, record: ReviewsAnalysisTagType): JSX.Element => (
                <ConcordanceLink query={record.tag}>{record.tag}</ConcordanceLink>
            ),
        },
        getRateColumn<ReviewsAnalysisTagType>(),
        getTotalWithPercentageColumn<ReviewsAnalysisTagType>(),
        getRepliedWithPercentageColumn<ReviewsAnalysisTagType>(),
        getAvgResponseDelayColumn<ReviewsAnalysisTagType>(timeUnit),
        getTrendColumn<ReviewsAnalysisTagType>(),
        getSentimentColumn<ReviewsAnalysisTagType>(),
    ];
}

export function userTagsPageTableColumns({
    timeUnit,
}: {
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum;
}): TableColumnsType<ReviwsAnalysisUserTagTableDataType> {
    return [
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__USER_TAG" />,
            dataIndex: 'title',
            key: 'title',
            render: (_value: unknown, record: ReviwsAnalysisUserTagTableDataType): JSX.Element => {
                return (
                    <NavigationLink
                        to={`${appRoute.reviewsManagementReviews.path}?ordering=-creation_date&tag-list=${record.ids}`}
                    >
                        {record.title}
                    </NavigationLink>
                );
            },
        },
        getRateColumn<ReviwsAnalysisUserTagTableDataType>(),
        getTotalColumn<ReviwsAnalysisUserTagTableDataType>(),
        getRepliedColumn<ReviwsAnalysisUserTagTableDataType>(),
        getAvgResponseDelayColumn<ReviwsAnalysisUserTagTableDataType>(timeUnit),
        getTrendColumn<ReviwsAnalysisUserTagTableDataType>(),
        getSentimentColumn<ReviwsAnalysisUserTagTableDataType>(),
    ];
}

export function topicsPageTableColumns({
    timeUnit,
}: {
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum;
}): TableColumnsType<TopicTableDataType> {
    return [
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__TOPIC" />,
            dataIndex: 'topic',
            key: 'topic',
            render: (_value: unknown, record: TopicTableDataType): JSX.Element => {
                if (!record.userTopic || !record.userTopicId) {
                    return <span>{record.topic}</span>;
                }

                return (
                    <NavigationLink to={generatePath(appRoute.reviewsAnalysisTopic.path, {id: record.userTopicId})}>
                        {record.topic}
                    </NavigationLink>
                );
            },
        },
        getRateColumn<TopicTableDataType>(),
        getTotalColumn<TopicTableDataType>(),
        getRepliedWithPercentageColumn<TopicTableDataType>(),
        getAvgResponseDelayColumn<TopicTableDataType>(timeUnit),
        getSentimentColumn<TopicTableDataType>(),
        getTrendColumn<TopicTableDataType>(),
        {
            align: 'center',
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__ACTIONS" />,
            dataIndex: 'action',
            key: 'action',
        },
    ];
}
